import {
    Avatar,
    Box,
    Card,
    CardActionArea,
    Chip,
    IconButton,
    Menu,
    MenuItem,
    Typography,
    alpha,
    useTheme
} from "@mui/material";
import { MoreVert } from "@mui/icons-material";
import { PopulatedAppUser } from "../../../../src/core/types/user/app-user.interface";

function getInitials(name: string): string {
    const words = name.trim().split(/\s+/).filter(Boolean);
    if (words.length >= 2) {
        return (words[0][0] + words[words.length - 1][0]).toUpperCase().slice(0, 2);
    }
    return name.slice(0, 2).toUpperCase() || "?";
}

function getColorFromString(str: string, palette: readonly string[]): string {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    return palette[Math.abs(hash) % palette.length];
}

interface ClientAppWithUrl {
    name: string;
    url?: string;
    icon?: { data: string; contentType: string };
}

interface AppUserWithClientAppUrl extends Omit<PopulatedAppUser, 'clientApp'> {
    clientApp: ClientAppWithUrl | null;
}

interface AppCardProps {
    app: AppUserWithClientAppUrl;
    anchorEl: { [key: string]: HTMLElement | null };
    onMenuOpen: (event: React.MouseEvent<HTMLElement>, appId: string) => void;
    onMenuClose: (appId: string) => void;
    onIconClick: (url?: string) => void;
}

export const AppCard = ({ app, anchorEl, onMenuOpen, onMenuClose, onIconClick }: AppCardProps) => {
    const theme = useTheme();

    if (!app.clientApp) {
        return null;
    }
    const clientApp = app.clientApp;

    return (
        <Card
            variant="subtle"
            sx={{
                borderRadius: { xs: 0, sm: 2 },
                overflow: 'visible',
                height: '100%',
                width: '100%',
                maxWidth: 140,
                display: 'flex',
                flexDirection: 'column',
                transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                '&:hover': clientApp.url
                    ? {
                          transform: { xs: 'none', sm: 'translateY(-2px)' },
                          boxShadow: {
                              xs: 'none',
                              sm: (t) =>
                                  `0 4px 12px ${alpha(t.palette.common.black, 0.15)}`
                          }
                      }
                    : {}
            }}
        >
            {clientApp.url ? (
                <CardActionArea
                    onClick={() => onIconClick(clientApp.url)}
                    sx={{ 
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'stretch',
                        p: 1.5,
                        height: '100%',
                        justifyContent: 'center',
                        '& .MuiCardActionArea-focusHighlight': {
                            bgcolor: alpha(theme.palette.primary.main, 0.12)
                        }
                    }}
                >
                    <Box sx={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, minHeight: 100 }}>
                        {app.roles.length > 0 && (
                            <IconButton
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onMenuOpen(e, app.id);
                                }}
                                sx={{ 
                                    position: 'absolute', 
                                    top: -4, 
                                    right: -4,
                                    bgcolor: 'background.paper',
                                    boxShadow: 1,
                                    '&:hover': { bgcolor: 'action.hover' }
                                }}
                                aria-label="Rollen anzeigen"
                            >
                                <MoreVert fontSize="small" />
                            </IconButton>
                        )}
                        <Avatar
                            src={clientApp.icon ? `data:${clientApp.icon.contentType};base64,${clientApp.icon.data}` : undefined}
                            sx={{
                                width: 44,
                                height: 44,
                                mb: 1.5,
                                border: 0,
                                bgcolor: clientApp.icon 
                                    ? 'transparent'
                                    : getColorFromString(clientApp.name, theme.palette.semantic!.avatarColors),
                                fontSize: '1rem',
                                fontWeight: 600,
                                p: 0,
                                borderRadius: 2,
                                flexShrink: 0,
                                '& img': {
                                    objectFit: 'contain',
                                    width: '100%',
                                    height: '100%',
                                    p: 0
                                }
                            }}
                        >
                            {!clientApp.icon && getInitials(clientApp.name)}
                        </Avatar>
                        <Typography
                            variant="body2"
                            sx={{
                                textAlign: 'center',
                                fontWeight: 500,
                                lineHeight: 1.4,
                                wordBreak: 'break-word',
                                px: 0.5
                            }}
                        >
                            {clientApp.name}
                        </Typography>
                    </Box>
                    {app.roles.length > 0 && (
                        <Menu
                            anchorEl={anchorEl[app.id]}
                            open={Boolean(anchorEl[app.id])}
                            onClose={() => onMenuClose(app.id)}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <MenuItem disabled>
                                <Typography variant="body2" color="text.secondary">
                                    Rollen:
                                </Typography>
                            </MenuItem>
                            {app.roles.map((role, i) => (
                                <MenuItem key={`role_${i}`} disabled>
                                    <Chip
                                        label={role.name}
                                        size="small"
                                        sx={{
                                            bgcolor: 'primary.main',
                                            color: 'primary.contrastText'
                                        }}
                                    />
                                </MenuItem>
                            ))}
                        </Menu>
                    )}
                </CardActionArea>
            ) : (
                <Box sx={{ p: 1.5, height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 100 }}>
                    {app.roles.length > 0 && (
                        <>
                            <IconButton
                                size="small"
                                onClick={(e) => onMenuOpen(e, app.id)}
                                sx={{ alignSelf: 'flex-end', mr: -1, mt: -1 }}
                            >
                                <MoreVert fontSize="small" />
                            </IconButton>
                            <Menu
                                anchorEl={anchorEl[app.id]}
                                open={Boolean(anchorEl[app.id])}
                                onClose={() => onMenuClose(app.id)}
                            >
                                <MenuItem disabled>
                                    <Typography variant="body2" color="text.secondary">
                                        Rollen:
                                    </Typography>
                                </MenuItem>
                                {app.roles.map((role, i) => (
                                    <MenuItem key={`role_${i}`} disabled>
                                        <Chip
                                            label={role.name}
                                            size="small"
                                            sx={{
                                                bgcolor: 'primary.main',
                                                color: 'primary.contrastText'
                                            }}
                                        />
                                    </MenuItem>
                                ))}
                            </Menu>
                        </>
                    )}
                    <Avatar
                        src={clientApp.icon ? `data:${clientApp.icon.contentType};base64,${clientApp.icon.data}` : undefined}
                        sx={{
                            width: 44,
                            height: 44,
                            mb: 1.5,
                            border: 0,
                            bgcolor: clientApp.icon 
                                ? 'transparent'
                                : 'action.hover',
                            color: 'text.secondary',
                            fontSize: '1rem',
                            fontWeight: 600,
                            p: 0,
                            borderRadius: 2,
                            flexShrink: 0,
                            '& img': {
                                objectFit: 'contain',
                                width: '100%',
                                height: '100%',
                                p: 0
                            }
                        }}
                    >
                        {!clientApp.icon && getInitials(clientApp.name)}
                    </Avatar>
                    <Typography variant="body2" sx={{ textAlign: 'center', fontWeight: 500, lineHeight: 1.4, px: 0.5 }}>
                        {clientApp.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                        Kein Link
                    </Typography>
                </Box>
            )}
        </Card>
    );
};
